import React, { useEffect, useState, useCallback } from 'react';
import { admin } from '../utils/api';
import { toast } from 'sonner';

const RANGE_OPTIONS = [
    { value: 'all', label: 'All Time' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
];

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('en-GB') : '—');

const fmtHoursSince = (hours) => {
    if (hours === null || hours === undefined) return '—';
    if (hours < 1) return '<1h ago';
    if (hours < 24) return `${Math.round(hours)}h ago`;
    return `${Math.round(hours / 24)}d ago`;
};

/**
 * AdminOnboardingFunnel — read-only, admin-only candidate onboarding
 * funnel view. Pulls from GET /admin/onboarding/{funnel,questions,candidates}
 * (new, additive, read-only — no existing admin endpoint touched).
 */
const AdminOnboardingFunnel = () => {
    const [range, setRange] = useState('all');
    const [funnel, setFunnel] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [candidateList, setCandidateList] = useState([]);
    const [stalledOnly, setStalledOnly] = useState(false);
    const [sort, setSort] = useState('last_active');
    const [loading, setLoading] = useState(true);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [funnelRes, questionsRes, candidatesRes] = await Promise.all([
                admin.getOnboardingFunnel(range),
                admin.getOnboardingQuestions(range),
                admin.getOnboardingCandidates(range, stalledOnly, sort),
            ]);
            setFunnel(funnelRes.data);
            setQuestions(questionsRes.data.questions || []);
            setCandidateList(candidatesRes.data.candidates || []);
        } catch (error) {
            toast.error('Failed to load onboarding funnel data');
        } finally {
            setLoading(false);
        }
    }, [range, stalledOnly, sort]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const maxCount = funnel?.funnel?.[0]?.count || 1;

    return (
        <div className="bg-white rounded-2xl p-6 border border-border/50 mt-8" data-testid="admin-onboarding-funnel-section">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h2 className="text-xl font-bold text-slate-900">Onboarding Funnel</h2>
                <div className="flex gap-2" data-testid="onboarding-range-filter">
                    {RANGE_OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            onClick={() => setRange(opt.value)}
                            data-testid={`onboarding-range-${opt.value}`}
                            className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                                range === opt.value ? 'text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                            style={range === opt.value ? { background: 'linear-gradient(135deg, #9333EA, #7C3AED)' } : {}}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="text-center text-slate-400 text-sm py-8" data-testid="onboarding-funnel-loading">Loading…</div>
            ) : (
                <>
                    {/* a) Sequential drop-off funnel */}
                    <div className="space-y-2 mb-8" data-testid="onboarding-funnel-chart">
                        {funnel?.funnel?.map((row) => (
                            <div key={row.step} data-testid={`onboarding-funnel-row-${row.step}`}>
                                <div className="flex items-center justify-between text-sm mb-1">
                                    <span className="font-medium text-slate-700">{row.label}</span>
                                    <span className="text-slate-500">
                                        <span className="font-bold text-slate-900">{row.count}</span>
                                        {row.drop_pct_from_previous !== null && (
                                            <span
                                                className={`ml-2 ${row.drop_pct_from_previous > 0 ? 'text-red-500' : 'text-slate-400'}`}
                                                data-testid={`onboarding-funnel-drop-${row.step}`}
                                            >
                                                -{Math.max(0, row.drop_pct_from_previous)}%
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <div className="w-full bg-slate-100 rounded-full h-2.5">
                                    <div
                                        className="h-2.5 rounded-full transition-all"
                                        style={{
                                            width: `${maxCount > 0 ? (row.count / maxCount) * 100 : 0}%`,
                                            background: 'linear-gradient(90deg, #9333EA, #7C3AED)',
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                        {funnel?.profile_edits && (
                            <div className="pt-3 mt-3 border-t border-slate-100 flex items-center justify-between text-sm" data-testid="onboarding-profile-edits-stat">
                                <span className="text-slate-500">Profile edits (optional, dashboard-only — not part of the funnel above)</span>
                                <span className="text-slate-700 font-medium">
                                    {funnel.profile_edits.started} started · {funnel.profile_edits.completed} completed
                                </span>
                            </div>
                        )}
                    </div>

                    {/* b) Assessment / microtest question breakdown */}
                    <div className="mb-8">
                        <h3 className="text-sm font-bold text-slate-700 mb-3">Microtest — where candidates stop</h3>
                        {questions.length === 0 ? (
                            <p className="text-sm text-slate-400" data-testid="onboarding-questions-empty">No question data yet.</p>
                                        ) : (
                            <div className="flex items-end gap-2 h-32" data-testid="onboarding-questions-chart">
                                {(() => {
                                    const qMax = Math.max(...questions.map((q) => q.count), 1);
                                    return questions.map((q) => (
                                        <div key={q.question_number} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`onboarding-question-bar-${q.question_number}`}>
                                            <span className="text-xs font-semibold text-slate-700 mb-1">{q.count}</span>
                                            <div
                                                className="w-full rounded-t"
                                                style={{ height: `${(q.count / qMax) * 100}%`, minHeight: 4, background: 'linear-gradient(180deg, #9333EA, #7C3AED)' }}
                                            />
                                            <span className="text-xs text-slate-400 mt-1">Q{q.question_number}</span>
                                        </div>
                                    ));
                                })()}
                            </div>
                        )}
                    </div>

                    {/* c) Candidate list — furthest step, last active, stalled filter */}
                    <div>
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                            <h3 className="text-sm font-bold text-slate-700">Candidates</h3>
                            <div className="flex flex-wrap items-center gap-3">
                                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="onboarding-stalled-filter-label">
                                    <input
                                        type="checkbox"
                                        checked={stalledOnly}
                                        onChange={(e) => setStalledOnly(e.target.checked)}
                                        data-testid="onboarding-stalled-filter-checkbox"
                                    />
                                    Stalled only (24h+ no activity)
                                </label>
                                <select
                                    value={sort}
                                    onChange={(e) => setSort(e.target.value)}
                                    className="text-sm border border-slate-200 rounded-lg px-2 py-1"
                                    data-testid="onboarding-sort-select"
                                >
                                    <option value="last_active">Last active (newest first)</option>
                                    <option value="last_active_asc">Last active (oldest first)</option>
                                </select>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="onboarding-candidates-table">
                                <thead>
                                    <tr className="text-left text-slate-400 border-b border-slate-100">
                                        <th className="py-2 pr-3 font-medium">Name</th>
                                        <th className="py-2 pr-3 font-medium">Email</th>
                                        <th className="py-2 pr-3 font-medium">Furthest Step</th>
                                        <th className="py-2 pr-3 font-medium">Last Active</th>
                                        <th className="py-2 pr-3 font-medium">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {candidateList.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="py-6 text-center text-slate-400" data-testid="onboarding-candidates-empty">
                                                No candidates found for this filter.
                                            </td>
                                        </tr>
                                    ) : (
                                        candidateList.map((c) => (
                                            <tr key={c.user_id} className="border-b border-slate-50" data-testid={`onboarding-candidate-row-${c.user_id}`}>
                                                <td className="py-2 pr-3 text-slate-800">{c.name || '—'}</td>
                                                <td className="py-2 pr-3 text-slate-600">{c.email || '—'}</td>
                                                <td className="py-2 pr-3 text-slate-700">
                                                    {c.furthest_step_label || '—'}
                                                    {c.inferred && (
                                                        <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500" data-testid={`onboarding-inferred-badge-${c.user_id}`}>
                                                            inferred
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-2 pr-3 text-slate-600">
                                                    {fmtHoursSince(c.hours_since_active)}
                                                    <span className="text-xs text-slate-400 ml-1">({fmtDateTime(c.last_active_at)})</span>
                                                </td>
                                                <td className="py-2 pr-3">
                                                    {c.is_stalled ? (
                                                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700" data-testid={`onboarding-stalled-badge-${c.user_id}`}>
                                                            Stalled
                                                        </span>
                                                    ) : (
                                                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-600">
                                                            Active
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminOnboardingFunnel;
